import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactApexChart from 'react-apexcharts';
import httpService from '../../../services/http.service';
import { ShimmerBlock, ShimmerRows } from '../../Shared/Shimmer';
import { PALETTE, formatInt, formatPercent } from './statsUtils';

const BADGES = [
    ['user_badge_goal', 'Objectif de vie', 'fa-bullseye'],
    ['user_badge_business', 'Travail', 'fa-briefcase'],
    ['user_badge_hobby', 'Loisirs', 'fa-gamepad'],
    ['user_badge_food', 'Nourriture', 'fa-cutlery'],
    ['user_badge_color', 'Couleurs', 'fa-paint-brush'],
];

const BadgeCard = ({ title, icon, rows, loading, emptyLabel, totalLabel }) => {
    const total = rows.reduce((sum, row) => sum + (Number(row.badge_users) || 0), 0);
    const chart = {
        series: rows.map((row) => Number(row.badge_users) || 0),
        options: {
            chart: { fontFamily: 'inherit' },
            labels: rows.map((row) => row.badge_value),
            colors: PALETTE,
            stroke: { width: 2, colors: ['#fff'] },
            dataLabels: { enabled: false },
            legend: { show: false },
            plotOptions: {
                pie: {
                    donut: {
                        size: '68%',
                        labels: { show: true, total: { show: true, label: totalLabel, formatter: () => formatInt(total) } },
                    },
                },
            },
            tooltip: { y: { formatter: (v) => formatInt(v) } },
        },
    };

    return (
        <div className="card stat-card">
            <div className="header">
                <h2><i className={`fa ${icon} mr-2 stats-card-icon`} aria-hidden="true" />{title}</h2>
            </div>
            <div className="body">
                {loading ? (
                    <>
                        <div className="d-flex justify-content-center mb-3"><ShimmerBlock width={170} height={170} radius="50%" /></div>
                        <ShimmerRows rows={3} />
                    </>
                ) : rows.length === 0 ? (
                    <div className="text-center text-muted py-5">{emptyLabel}</div>
                ) : (
                    <>
                        <ReactApexChart options={chart.options} series={chart.series} type="donut" height={230} />
                        <ul className="legend-list">
                            {rows.map((row, index) => (
                                <li key={row.badge_value}>
                                    <span className="legend-list__dot" style={{ background: PALETTE[index % PALETTE.length] }} />
                                    <span className="legend-list__name">{row.badge_value}</span>
                                    <span className="legend-list__value">{formatInt(row.badge_users)}</span>
                                    <span className="legend-list__share">{formatPercent(row.pourcentage)}</span>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        </div>
    );
};

/** Répartition des utilisateurs par type de badge (un anneau et sa légende chiffrée par catégorie). */
const UsersByBadge = () => {
    const { t } = useTranslation();
    const [state, setState] = useState({ loading: true, byBadge: {} });

    useEffect(() => {
        let cancelled = false;
        Promise.allSettled(BADGES.map(([type]) => httpService.get(`/charts/userByBadge?badge_type=${type}`))).then((results) => {
            if (cancelled) return;
            const byBadge = {};
            results.forEach((result, index) => {
                const data = result.status === 'fulfilled' ? result.value?.data : [];
                byBadge[BADGES[index][0]] = Array.isArray(data) ? data : [];
            });
            setState({ loading: false, byBadge });
        });
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="row clearfix equal-height-row">
            {BADGES.map(([type, label, icon]) => (
                <div className="col-xl-4 col-md-6 col-12" key={type}>
                    <BadgeCard
                        title={t(label)}
                        icon={icon}
                        rows={state.byBadge[type] || []}
                        loading={state.loading}
                        emptyLabel={t('Aucune donnée à afficher.')}
                        totalLabel={t('Total')}
                    />
                </div>
            ))}
        </div>
    );
};

export default UsersByBadge;
